<template>
  <div class="main-content-container customers-list-container-admin">
    <ViewHeader title="Company">
      <div class="filter-by-container">
        <p class="d-inline secondary--text position-relative">Filter By</p>

        <div class="d-flex">
          <b-form-group class="mr-3" style="width: 20rem">
            <b-form-select
              v-model="filter.companyId"
              :options="companyNames"
              class="cy-clist-company-name"
              data-cy="company-list-filter-name"
              @change="reloadList"
            ></b-form-select>
          </b-form-group>

          <b-form-group class="mr-3">
            <b-form-select
              v-model="filter.headroomEntity"
              :options="headroomEntityOptions"
              class="cy-clist-sub-type"
              data-cy="company-list-filter-company-headroom-entity"
              @change="reloadList"
            ></b-form-select>
          </b-form-group>
        </div>
      </div>
    </ViewHeader>

    <CompaniesViewTabs @change-tab="changeTab" style="height: 30vh">
      <b-row
        v-if="canShowList"
        class="mx-0 mt-5"
        data-cy="company-list-row-wrapper"
      >
        <CompanyTableHeader />

        <CompanyTableRow
          :ref="id"
          v-for="{
            id,
            name,
            logo,
            billingEmail,
            headroomEntity,
            companyMembership,
            totalUsers,
            isActive,
          } in displayedCompanies"
          :key="id"
          :id="id"
          :name="name"
          :logo="logo"
          :headroomEntity="headroomEntity"
          :billingEmail="billingEmail"
          :companyMembership="companyMembership"
          :totalUsers="totalUsers"
          :isArchived="!isActive"
          :memberships="memberships"
          :membershipLabel="
            getMembershipCustomLabel(companyMembership.membershipDetails)
          "
          :subscriptionError="subscriptionError"
          :canCloseSubModal="canCloseSubModal"
          @go-to-customers="goToCustomers(getCompanyById(id))"
          @edit-company="showEditModal(getCompanyById(id))"
          @archive-company="
            showArchiveModal(getCompanyById(id), { isUnArchive: false })
          "
          @unarchive-company="
            showArchiveModal(getCompanyById(id), { isUnArchive: true })
          "
          @delete-company="showDeleteModal(getCompanyById(id))"
          @confirm-update-subscription="updateCompany($event, id)"
          @cancel-update-subscription="clearSubscriptionError"
        />
      </b-row>

      <infinite-loading
        ref="infiniteLoading"
        spinner="spiral"
        :identifier="infiniteID"
        @infinite="loadNextPage"
        ><div slot="no-more">
          <h4 class="font-md primary--text font-family-medium mt-5 mb-5">
            No more companies to load.
          </h4>
        </div>
        <div slot="no-results">
          <h4 class="font-md primary--text font-family-medium mt-5 mb-5">
            No more companies to load.
          </h4>
        </div>
      </infinite-loading>
    </CompaniesViewTabs>

    <AddCompanyButton @click="openCleanedEditCompanyModal" />

    <EditCompanyModal
      :isOpen="edit.isModalOpen"
      :isProcessing="isProcessing"
      :updateError="edit.updateError"
      :id="selectedCompany.id"
      :name="selectedCompany.name"
      :logo="selectedCompany.logo"
      :billingEmail="selectedCompany.billingEmail"
      :headroomEntity="selectedCompany.headroomEntity"
      :country="selectedCompany.country"
      :memberships="memberships"
      @close-company-modal="closeModal('edit')"
      @confirm-update="updateCompany($event, selectedCompany.id)"
    />

    <ArchiveModal
      v-if="checkIfExistingCompany(selectedCompany.id)"
      :isOpen="archive.isModalOpen"
      :isProcessing="isProcessing"
      :isUnArchive="archive.isUnArchive"
      :archiveError="archive.archiveError"
      :id="selectedCompany.id"
      :name="selectedCompany.name"
      :subscriptionType="
        selectedCompany.companyMembership.membershipDetails.title
      "
      :avatartThumbnail="selectedCompany.avatartThumbnail"
      @close-archive-modal="closeModal('archive')"
      @confirm-archive="archiveCompany"
      @confirm-unarchive="unArchiveCompany"
    />

    <DeleteCompanyModal
      v-if="checkIfExistingCompany(selectedCompany.id)"
      :modal="deleteModalData"
      @close-delete-modal="closeModal('delete')"
      @confirm-delete="deleteCompany"
    />

    <SaveIndicator ref="saveIndicator" />
    <AlertModal ref="alert" />
  </div>
</template>

<script>
import SaveIndicator from "@/components/indicators/SaveIndicator.vue";

import ViewHeader from "./ViewHeader.vue";
import CompaniesViewTabs from "./CompaniesViewTabs.vue";
import AddCompanyButton from "./AddCompanyButton.vue";
import CompanyTableHeader from "./CompanyTableHeader.vue";
import CompanyTableRow from "./CompanyTableRow.vue";
import EditCompanyModal from "./EditCompanyModal.vue";
import ArchiveModal from "./ArchiveModal.vue";
import DeleteCompanyModal from "./DeleteCompanyModal.vue";
import AlertModal from "./AlertModal.vue";

import {
  getCompanies,
  getCompaniesMinimal,
  updateCompany,
  updateCompanyImage,
  getMemberships,
  createCompany,
  deleteCompany
} from "@/apis/companies/index";

export default {
  name: "CompanyList",

  components: {
    SaveIndicator,
    AlertModal,
    ViewHeader,
    CompaniesViewTabs,
    AddCompanyButton,
    CompanyTableHeader,
    CompanyTableRow,
    EditCompanyModal,
    ArchiveModal,
    DeleteCompanyModal
  },

  data() {
    return {
      isProcessing: false,
      showSaveIndicator: null,

      // * for inifnite scroll
      infiniteID: +new Date(),
      pageOffset: 0,

      filter: {
        companyId: null,
        headroomEntity: null
      },

      headroomEntityOptions: [
        { value: null, text: "Please select a Billing Entity" },
        { value: "EU", text: "Public Support Assistance BV (EU)" },
        { value: "US", text: "Public Support Assistance Services LLC (US)" }
      ],

      companyNames: [],

      selectedCompany: {},

      subscriptionError: "",
      canCloseSubModal: true,

      currentTab: "active", // or "archived"

      edit: {
        isModalOpen: false,
        updateError: ""
      },

      archive: {
        isModalOpen: false,
        archiveError: "",
        isUnArchive: false
      },

      delete: {
        isModalOpen: false,
        deleteError: "",
        isDeleted: false
      },

      companies: [],

      memberships: []
    };
  },

  async created() {
    await this.setCompaniesNames(this.isActiveTab);
    await this.setMemberships();
  },

  computed: {
    canShowList() {
      return this.companies?.length && this?.memberships.length;
    },

    displayedCompanies() {
      return this.isActiveTab ? this.activeCompanies : this.archivedCompanies;
    },

    activeCompanies() {
      this.trackActiveCompanyView();
      return this.companies.filter(({ isActive }) => !!isActive);
    },

    archivedCompanies() {
      this.trackArchivedCompanyView();
      return this.companies.filter(({ isActive }) => !isActive);
    },

    isActiveTab() {
      return this.currentTab === "active";
    },

    deleteModalData() {
      return {
        isOpen: this.delete.isModalOpen,
        isProcessing: this.isProcessing,
        isDeleted: this.delete.isDeleted,
        deleteError: this.delete.deleteError,
        id: this.selectedCompany.id,
        name: this.selectedCompany.name,
        logo: this.selectedCompany.logo,
        subscriptionType: this.selectedCompany.companyMembership?.membershipDetails?.title || ''
      };
    }
  },

  methods: {
    trackActiveCompanyView() {
      this.$gtag.event("Active Company - list", {
        event_category: "feature"
      });
    },
    trackArchivedCompanyView() {
      this.$gtag.event("Archived Company - list", {
        event_category: "feature"
      });
    },
    async loadNextPage($state) {
      const PAGE_SIZE = 30;

      const {
        response: { data: { results: companies, next, offset } = {} } = {},
        error
      } = await this.asyncWrap(
        getCompanies,
        PAGE_SIZE,
        this.pageOffset,
        null,
        this.isActiveTab
      );

      if (error) {
        $state.complete();
        this.$refs.alert.open(error);
        return;
      }

      // * We need to initialise the list of companies at it is used in subsequent methods
      this.companies = [...this.companies, ...companies];

      const hasFilters = Object.values(this.filter).some(Boolean);
      if (hasFilters) {
        const { companyId, headroomEntity } = this.filter;
        this.companies = this.getFilteredList(
          companies,
          companyId,
          headroomEntity
        );
      }

      this.pageOffset = offset;

      next ? $state.loaded() : $state.complete();
    },

    getFilteredList(list, companyId, headroomEntity) {
      const hasBoth = !!companyId && headroomEntity;
      if (hasBoth) {
        const company = this.companies.find(({ id, headroomEntity: HE }) => {
          return id === companyId && HE === headroomEntity;
        });

        const hasNoCompany = !company;
        return hasNoCompany ? [] : [company];
      }

      if (companyId) {
        const company = this.getCompanyById(companyId);
        const hasNoCompany = !company;
        return hasNoCompany ? [] : [company];
      }

      if (headroomEntity) {
        const filteredCompanies = this.getCompaniesByEntity(headroomEntity);
        return [...filteredCompanies];
      }

      return [...list];
    },

    getCompanyById(targetId) {
      return this.companies.find(({ id }) => id === targetId);
    },

    getCompaniesByEntity(entity) {
      return this.companies.filter(
        ({ headroomEntity }) => entity === headroomEntity
      );
    },

    async setMemberships() {
      const {
        response: { data: { results: memberships } = {} } = {},
        error
      } = await this.asyncWrap(getMemberships);

      if (error) {
        const message = `
        Failed to load the list of memberships.
        Please refresh to try again. If the problem persists, kindly contact
        your admin.`;
        this.$refs.alert.open(message);
        return;
      }

      const list = memberships.map(this.transformMembershipLabel);
      const sorted = this.sortByText(list);

      const empty = { value: null, text: "Select a membership" };
      this.memberships = [empty, ...sorted];
    },

    sortByText(list) {
      return list.sort((a, b) => {
        const textA = a.text.toUpperCase();
        const textB = b.text.toUpperCase();

        if (textA < textB) {
          return -1;
        }

        if (textA > textB) {
          return 1;
        }

        return 0;
      });
    },

    transformMembershipLabel({
      id,
      title,
      totalHours,
      allowedFlexibleHours,
      maxSeats
    }) {
      const text = this.getMembershipCustomLabel({
        title,
        totalHours,
        allowedFlexibleHours,
        maxSeats
      });

      return {
        value: id,
        text: text,
        allowedFlexibleHours,
        maxSeats
      };
    },

    getMembershipCustomLabel({
      title,
      totalHours,
      allowedFlexibleHours,
      maxSeats
    }) {
      let seatsPrefix = maxSeats ? maxSeats : "unlimited";
      let hoursPrefix = totalHours ? totalHours : "unlimited";
      const seatOrSeats = maxSeats > 1 || maxSeats === 0 ? "seats" : "seat";

      if (allowedFlexibleHours) {
        hoursPrefix = "flexible";
      }

      return `${title} (${hoursPrefix} hours & ${seatsPrefix} ${seatOrSeats})`;
    },

    async setCompaniesNames(getActiveOnes = true) {
      const {
        response: { data: { results: names } = {} } = {},
        error
      } = await this.asyncWrap(getCompaniesMinimal, getActiveOnes);

      if (error) {
        const message = `
        Failed to load the list of company names.
        Please refresh to try again. If the problem persists, kindly contact
        your admin.`;
        this.$refs.alert.open(message);
        return;
      }

      this.companyNames = names.map(({ id, name }) => ({
        value: id,
        text: name
      }));
      this.companyNames.unshift({ value: null, text: "Select a company" });
    },

    async reloadList() {
      this.clearCompanyListForInfiniteScroll();
      this.updateFilterLists();
    },

    changeTab(tab) {
      this.currentTab = tab;
      this.clearCompanyListForInfiniteScroll();
      this.clearFilterSelection();
      this.updateFilterLists();
    },

    // * called when the filter is updated or when tab is changed.
    clearCompanyListForInfiniteScroll() {
      this.pageOffset = 0;
      this.companies = [];
      this.infiniteID += 1;
    },

    updateFilterLists() {
      this.setCompaniesNames(this.isActiveTab);
      // * Note that billing entities are currently harcoded
      // * Hence they are not refreshed.
    },

    clearFilterSelection() {
      this.filter.companyId = null;
      this.filter.headroomEntity = null;
    },

    showEditModal(item) {
      this.selectedCompany = { ...item };
      this.edit.isModalOpen = true;
    },

    openCleanedEditCompanyModal() {
      this.selectedCompany = {};
      this.edit.isModalOpen = true;
    },

    showArchiveModal(item, { isUnArchive } = null) {
      this.selectedCompany = { ...item };
      this.archive.isModalOpen = true;
      this.archive.archiveError = "";
      this.archive.isUnArchive = isUnArchive;
    },

    closeModal(modal) {
      this[modal].isModalOpen = false;
      this.selectedCompany = {};
    },

    async updateCompany({ data, blob, fileName }, companyId) {
      this.$refs.saveIndicator.showSaveIndicator();
      this.canCloseSubModal = false;

      const index = companyId
        ? this.companies.findIndex(({ id }) => id === companyId)
        : this.companies.length;

      // * only update the logo if company already exists.
      if (blob && companyId) {
        const {
          response: { data: updatedCompany } = {},
          error
        } = await this.asyncWrap(updateCompanyImage, companyId, blob, fileName);

        this.$refs.saveIndicator.clearSaveIndicators();

        if (error) {
          console.error(error);
          const message =
            "Failed to update the company logo. Please try again. If the problem persists, please contact your admin.";
          // todo: display error in `EditCompanyModal.vue`
          this.$refs.alert.open(message);
          return;
        }

        this.$set(this.companies, index, {
          ...this.companies[index],
          ...updatedCompany
        });
      }

      if (data) {
        const createData = this.cleanCreateData(data);
        const updateData = this.cleanUpdateData(data);
        var { new_membership, total_hours } = this.extractMembershipData(data);

        const method = companyId ? updateCompany : createCompany;
        const params = companyId ? [companyId, updateData] : [createData];
        const {
          response: { data: updatedCompany } = {},
          error
        } = await this.asyncWrap(method, ...params);

        this.$refs.saveIndicator.clearSaveIndicators();

        if (error) {
          this.handleErrors(error);
          return;
        }

        // * We reach here after skipping the check for `blob` and `companyId` values both available
        // * which means that either there were no id or no blob.
        // * We are interested in the case where the company is being created
        // * hence no id available in the conditions above. The id becomes available only after
        // * the company gets created.
        // * Thus, if the freshly created company has no logo value AND there is a blob present
        // * we need to set its logo right away.
        const isLogoForNewCompany = !updatedCompany.logo && !!blob;
        if (isLogoForNewCompany) {
          await this.updateCompany(
            { data: { id: updatedCompany.id }, blob, fileName },
            updatedCompany.id
          );
        }

        // * we are redefining `new_membership`, hence using var on purpose here
        // * we need to ignore the eslint warning
        // eslint-disable-next-line
        var { hasChanged, new_membership } = this.checkIfMembershipHasChanged(
          updatedCompany,
          new_membership
        );
        if (hasChanged) {
          const updatedMembership = {
            id: updatedCompany.id,
            new_membership,
            total_hours
          };
          // * recursive call to set the membership
          // * method `checkIfMembershipHasChanged` sets the exit condition.
          await this.updateCompany(
            { data: updatedMembership },
            updatedMembership.id
          );
        }

        this.reloadList();
      }

      this.subscriptionError = "";
      this.canCloseSubModal = true;

      this.edit.isModalOpen = false;
      this.archive.isModalOpen = false;
    },

    capitalise(text) {
      const [first, ...rest] = text;
      return `${first.toUpperCase()}${rest.join("")}`;
    },

    handleErrors(error) {
      console.error(error?.response?.data.nonFieldErrors);

      const nonFieldErrors = error?.response?.data?.nonFieldErrors;
      if (nonFieldErrors) {
        const isTotalCustomersExceededArchive =
          nonFieldErrors[0] ===
          "Company cannot be archived because it has users.";
        if (isTotalCustomersExceededArchive) {
          this.archive.archiveError = nonFieldErrors[0];
          return;
        }

        const isTotalCustomersExceededChange =
          nonFieldErrors[0] ===
          "Company have more users than the membership allows.";

        if (isTotalCustomersExceededChange) {
          this.subscriptionError = nonFieldErrors[0];
        }

        this.errors = nonFieldErrors;
        return;
      }

      if (error) {
        console.error(error);
        const errorMessage = this.capitalise(error?.response?.data?.name[0]);
        const containsExist = errorMessage.includes("exist");
        const message =
          errorMessage ||
          "Failed to save company. Please try again. If the problem persists, please contact your admin.";

        containsExist
          ? (this.edit.updateError = message)
          : this.$refs.alert.open(message);
        return;
      }
    },

    cleanCreateData(data) {
      // * `new_membership` & `total_hours` are defined for destructuring
      // * we know we only have use for the rest of the data.
      // eslint-disable-next-line
      const { new_membership, total_hours, ...noMembershipData } = data;
      return noMembershipData;
    },

    cleanUpdateData(data) {
      // * if used, `total_hours` will be present in the `data` object
      // * along side the `new_membership` ; else we exclude both.
      // eslint-disable-next-line
      const { new_membership, total_hours, ...noMembershipData } = data;
      const noEmptyMembershipFieldData = new_membership
        ? data
        : noMembershipData;
      return noEmptyMembershipFieldData;
    },

    extractMembershipData(data) {
      const { new_membership, total_hours } = data;
      return { new_membership, total_hours };
    },

    checkIfMembershipHasChanged(updatedCompany, new_membership) {
      if (!new_membership) {
        return { hasChanged: false, new_membership: null };
      }

      const currentId =
        updatedCompany?.companyMembership?.membershipDetails?.id;

      const isSame = currentId === new_membership;
      if (isSame) {
        return { hasChanged: false, new_membership: null };
      }

      return { hasChanged: true, new_membership };
    },

    clearSubscriptionError() {
      this.subscriptionError = "";
    },

    async archiveCompany(itemId) {
      const data = { is_active: false };
      await this.updateCompany({ data }, itemId);
    },

    async unArchiveCompany(itemId) {
      const data = { is_active: true };
      await this.updateCompany({ data }, itemId);
    },

    checkIfExistingCompany(id) {
      return id;
    },

    goToCustomers(item) {
      this.$router.push({
        name: "CustomersListAdmin",
        query: {
          tab: 0,
          companyId: item.id
        }
      });
    },

    showDeleteModal(item) {
      this.selectedCompany = { ...item };
      this.delete.isModalOpen = true;
      this.delete.deleteError = "";
      this.delete.isDeleted = false;
    },

    async deleteCompany() {
      this.$refs.saveIndicator.showSaveIndicator();
      this.isProcessing = true;

      try {
        await this.asyncWrap(deleteCompany, this.selectedCompany.id);
        this.delete.isDeleted = true;
        this.companies = this.companies.filter(company => company.id !== this.selectedCompany.id);
        
        setTimeout(() => {
          this.closeModal('delete');
          this.reloadList();
        }, 2000);
      } catch (error) {
        console.error(error);
        this.delete.deleteError = error.response?.data?.detail || "Failed to delete company";
      } finally {
        this.isProcessing = false;
        this.$refs.saveIndicator.clearSaveIndicators();
      }
    },

    // * Having the method defined locally for now. Will be moved
    // * once the pattern is approved.
    async asyncWrap(fn, ...args) {
      let response;
      let error;

      this.isProcessing = true;

      try {
        response = await fn(...args);
      } catch (caughtError) {
        error = caughtError;
      }

      this.isProcessing = false;

      return { response, error };
    }
  }
};
</script>
